import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Article } from '../../../../../types/article.types';
import { getArticlesByWebsite } from '../../../../articles/api';

interface ArticlePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (articles: Article[]) => void;
  websiteId: string;
  excludeArticleIds?: string[];
}

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  } catch {
    return dateString;
  }
};

const ArticlePickerDialog: React.FC<ArticlePickerDialogProps> = ({
  open,
  onClose,
  onSelect,
  websiteId,
  excludeArticleIds = [],
}) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [selectedArticles, setSelectedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch articles when dialog opens
  useEffect(() => {
    if (open && websiteId) {
      fetchArticles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, websiteId]);

  // Filter articles based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredArticles(articles);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = articles.filter(
        article =>
          article.headline.toLowerCase().includes(term) ||
          article.journalistName?.toLowerCase().includes(term) ||
          article.status?.toLowerCase().includes(term)
      );
      setFilteredArticles(filtered);
    }
    setPage(0); // Reset to first page when search changes
  }, [searchTerm, articles]);

  const fetchArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getArticlesByWebsite(websiteId);
      
      // Handle response format: check if data has an 'articles' property, otherwise assume it's already an array
      const articlesArray = Array.isArray(data) ? data : ((data as any)?.articles && Array.isArray((data as any).articles) ? (data as any).articles : []);
      
      // Filter out already excluded articles and sort by publish date (newest first)
      const availableArticles = articlesArray
        .filter((article: Article) => !excludeArticleIds.includes(article.id))
        .sort((a: Article, b: Article) => {
          const dateA = new Date(a.publishDate).getTime();
          const dateB = new Date(b.publishDate).getTime();
          return dateB - dateA; // Descending order (newest first)
        });
      
      setArticles(availableArticles);
      setFilteredArticles(availableArticles);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const pageArticles = filteredArticles.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );
      const newSelected = [...selectedArticles];
      pageArticles.forEach(article => {
        if (!newSelected.find(a => a.id === article.id)) {
          newSelected.push(article);
        }
      });
      setSelectedArticles(newSelected);
    } else {
      const pageArticleIds = filteredArticles
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(a => a.id);
      setSelectedArticles(selectedArticles.filter(a => !pageArticleIds.includes(a.id)));
    }
  };

  const handleSelectArticle = (article: Article) => {
    const isSelected = selectedArticles.some(a => a.id === article.id);
    if (isSelected) {
      setSelectedArticles(selectedArticles.filter(a => a.id !== article.id));
    } else {
      setSelectedArticles([...selectedArticles, article]);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleConfirm = () => {
    onSelect(selectedArticles);
    setSelectedArticles([]);
    setSearchTerm('');
  };

  const handleClose = () => {
    setSelectedArticles([]);
    setSearchTerm('');
    onClose();
  };

  const pageArticles = filteredArticles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const numSelected = selectedArticles.length;
  const pageArticleIds = pageArticles.map(a => a.id);
  const numPageSelected = selectedArticles.filter(a => pageArticleIds.includes(a.id)).length;
  const isPageFullySelected = pageArticles.length > 0 && numPageSelected === pageArticles.length;
  const isPagePartiallySelected = numPageSelected > 0 && numPageSelected < pageArticles.length;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      <DialogTitle>
        Select Articles to Add
        {numSelected > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {numSelected} article{numSelected !== 1 ? 's' : ''} selected
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
        {/* Search bar */}
        <Box sx={{ px: 3, pt: 2, pb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by title, journalist, or status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ px: 3, pb: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : filteredArticles.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="text.secondary">
              {searchTerm ? 'No articles match your search.' : 'No articles available.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isPagePartiallySelected}
                        checked={isPageFullySelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell><strong>Title</strong></TableCell>
                    <TableCell><strong>Journalist</strong></TableCell>
                    <TableCell><strong>Published</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageArticles.map((article) => {
                    const isSelected = selectedArticles.some(a => a.id === article.id);
                    return (
                      <TableRow
                        key={article.id}
                        hover
                        onClick={() => handleSelectArticle(article)}
                        selected={isSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={isSelected} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {article.headline}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {article.journalistName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {article.publishDate ? formatDate(article.publishDate) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={article.status || 'Unknown'}
                            size="small"
                            color={article.status === 'published' ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredArticles.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={numSelected === 0}
        >
          Add {numSelected > 0 ? `${numSelected} ` : ''}Article{numSelected !== 1 ? 's' : ''} to Podcast
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArticlePickerDialog;
